import {
    formNameLabel,
    scheduleEmbedLabel,
    schedulePreviewEmbed,
} from "@/features/navigation/services/entityDisplayLabel";
import { supabaseClient } from "@/lib/supabase";
import { queryOptions, useQuery } from "@tanstack/react-query";

export interface PendingValidationT {
    validation_id: string;
    created_at: string;
    entity_type: "form" | "schedule";
    entity_id: string;
    entity_name: string;
    /** When the form or schedule content last changed. */
    calculated_updated_at: string | null;
}

export interface PendingValidationsPage {
    rows: PendingValidationT[];
    total: number;
}

export const PENDING_VALIDATIONS_PAGE_SIZE = 20;

const pendingValidationSelect = `validation_id, created_at, form_validations(form_id, forms(form_name, slug, calculated_updated_at)), schedule_validations(schedule_id, schedules(calculated_updated_at, ${schedulePreviewEmbed}))`;

/** A validator's own outstanding to-dos - validations assigned to them that haven't been actioned yet, page-based. */
export async function selectMyPendingValidations(
    userId: string,
    page: number,
    pageSize: number = PENDING_VALIDATIONS_PAGE_SIZE
): Promise<PendingValidationsPage> {
    const rangeFrom = page * pageSize;
    const rangeTo = rangeFrom + pageSize - 1;

    const { data, error, count } = await supabaseClient
        .from("validations")
        .select(pendingValidationSelect, { count: "exact" })
        .eq("validated_by", userId)
        .is("is_valid", null)
        .order("created_at", { ascending: false })
        .range(rangeFrom, rangeTo);
    if (error) throw error;

    const rows = (data ?? []).flatMap((row): PendingValidationT[] => {
        const formLink = row.form_validations?.[0];
        if (formLink?.form_id) {
            return [
                {
                    validation_id: row.validation_id,
                    created_at: row.created_at,
                    entity_type: "form",
                    entity_id: formLink.form_id,
                    entity_name: formNameLabel(formLink.forms),
                    calculated_updated_at: formLink.forms?.calculated_updated_at ?? null,
                },
            ];
        }
        const scheduleLink = row.schedule_validations?.[0];
        if (scheduleLink?.schedule_id) {
            return [
                {
                    validation_id: row.validation_id,
                    created_at: row.created_at,
                    entity_type: "schedule",
                    entity_id: scheduleLink.schedule_id,
                    entity_name: scheduleEmbedLabel(scheduleLink.schedules),
                    calculated_updated_at: scheduleLink.schedules?.calculated_updated_at ?? null,
                },
            ];
        }
        return [];
    });

    return { rows, total: count ?? 0 };
}

export const myPendingValidationsQueryOptions = (
    userId: string,
    page: number = 0,
    pageSize: number = PENDING_VALIDATIONS_PAGE_SIZE
) =>
    queryOptions({
        queryKey: ["validations", "pending", userId, page, pageSize],
        queryFn: () => selectMyPendingValidations(userId, page, pageSize),
        enabled: Boolean(userId),
    });

export function useMyPendingValidationsQuery(
    userId: string,
    page: number = 0,
    pageSize: number = PENDING_VALIDATIONS_PAGE_SIZE
) {
    return useQuery(myPendingValidationsQueryOptions(userId, page, pageSize));
}

/** Outstanding items assigned to this user. Used for the Validate nav badge. */
export async function selectMyPendingValidationCount(userId: string): Promise<number> {
    const { count, error } = await supabaseClient
        .from("validations")
        .select("validation_id", { count: "exact", head: true })
        .eq("validated_by", userId)
        .is("is_valid", null);
    if (error) throw error;
    return count ?? 0;
}

export const myPendingValidationCountQueryOptions = (userId: string) =>
    queryOptions({
        queryKey: ["validations", "pending-count", userId],
        queryFn: () => selectMyPendingValidationCount(userId),
        enabled: Boolean(userId),
    });

export function useMyPendingValidationCount(userId: string) {
    return useQuery(myPendingValidationCountQueryOptions(userId));
}
